import type { Dayjs } from 'dayjs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import AMP_KEYS from '@/configs/amp-track-keys';
import { CHART_FREQUENCY } from '@/entities/chart/model/constants';
import { ChartFrequencyMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import useStates from '@/hooks/pure/useStates';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import { SEARCH_RESULT_STATUS, SummaryStatus } from '../types/searchConst';

export const SearchDetailContext = createContext<SearchFilterReturnT>({
  filterQueryFormats: [],
  originalFilterQueryFormats: [],
  dateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
  originalDateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
  keywordFilter: null,
  frequency: CHART_FREQUENCY.DAY,
  originalKeywordFilter: null,
  originalAiKeywordFilter: '',
  filterChanged: false,
  columns: {},
  alias: '',
  aiKeywordFilter: '',
  isFilterEmpty: false,
  searchResult: {
    title: '',
    alias: '',
    options: {
      trend_frequency: CHART_FREQUENCY.DAY,
    },
    filter: undefined,
    created_at: '',
    created_by: '',
    owner: false,
    summary_status: SummaryStatus.DONE,
    paginate_token: '',
    summary: '',
    summary_issues: [],
    semantic_result_status: SEARCH_RESULT_STATUS.DONE,
  },
  updateFrequency: (value: CHART_FREQUENCY) => {},
  updateAiKeywordFilter: (value: string) => {},
  updateKeywordFilter: (
    value: StructuredFilterStringNumberValueSet | null,
  ) => {},
  updateDataFilter: (value: StructuredFilterQueryFormatT[]) => {},
  updateDateFilter: (value: [Dayjs, Dayjs]) => {},

  updateOriginalAIKeywordFilter: (value: string) => {},
  updateOriginalKeywordFilter: (
    value: StructuredFilterStringNumberValueSet | null,
  ) => {},
  updateOriginalFilterQueryFormats: (
    value: StructuredFilterQueryFormatT[],
  ) => {},
  updateOriginalDateRange: (value: [Dayjs, Dayjs]) => {},

  clearFilter: () => {},
});

const SearchDetailProvider = ({
  children,
  searchResult,
}: {
  children: React.ReactNode;
  searchResult: GlobalSearchResult;
}) => {
  const { parseStructuredFilter } = useStructuredFilter();
  const initialized = useRef(false);

  const {
    state: filterState,
    setState,
    setValue,
  } = useStates<SearchFilterStateT>({
    frequency: CHART_FREQUENCY.DAY,
    aiKeywordFilter: '',
    filterQueryFormats: [],
    originalFilterQueryFormats: [],
    originalAiKeywordFilter: '',
    dateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
    originalDateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
    keywordFilter: null,
    originalKeywordFilter: null,
  });

  const columns = useStructuredFilterMap();

  const initializeFilter = useCallback(
    async (searchResult?: GlobalSearchResult) => {
      const sFilter = new SFilter(searchResult?.filter);

      const dateRangeFilter = sFilter.getDateFilter();

      const dateRange: [Dayjs, Dayjs] = [
        getTimezoneDayjs(dateRangeFilter?.start_date || undefined),
        getTimezoneDayjs(dateRangeFilter?.end_date || undefined),
      ];

      const keywordFilter = sFilter.getSearchKeywordFilter();

      const filterMap = sFilter.getDataFilterMap();

      const filterQueryFormats = parseStructuredFilter(filterMap);

      setState({
        aiKeywordFilter: searchResult?.query || '',
        filterQueryFormats: filterQueryFormats,
        originalFilterQueryFormats: filterQueryFormats,
        dateRange: dateRange,
        originalDateRange: dateRange,
        frequency: searchResult?.options.trend_frequency,
        keywordFilter: keywordFilter,
        originalKeywordFilter: keywordFilter,
        originalAiKeywordFilter: searchResult?.query || '',
      });
    },
    [parseStructuredFilter, setState],
  );

  useEffect(() => {
    if (initialized.current) return;

    initialized.current = true;
    initializeFilter(searchResult);
  }, [initializeFilter, JSON.stringify(searchResult)]);

  const clearFilter = () => {
    setState((prevState) => ({
      ...prevState,
      filterQueryFormats: [],
    }));
  };

  const dataFilterChanged =
    JSON.stringify(filterState.filterQueryFormats) !==
    JSON.stringify(filterState.originalFilterQueryFormats);
  const dateFilterChanged =
    JSON.stringify(filterState.dateRange) !==
    JSON.stringify(filterState.originalDateRange);
  const keywordFilterChanged =
    JSON.stringify(filterState.keywordFilter) !==
    JSON.stringify(filterState.originalKeywordFilter);
  const aiKeywordFilterChanged =
    filterState.aiKeywordFilter !== filterState.originalAiKeywordFilter;
  const frequencyChanged =
    filterState.frequency !== searchResult.options.trend_frequency;

  const filterChanged =
    dataFilterChanged ||
    dateFilterChanged ||
    keywordFilterChanged ||
    aiKeywordFilterChanged ||
    frequencyChanged;

  const isFilterEmpty =
    filterState.filterQueryFormats.length === 0 &&
    filterState.keywordFilter === null &&
    filterState.aiKeywordFilter === '';

  return (
    <SearchDetailContext.Provider
      value={{
        ...filterState,
        updateFrequency: (value: CHART_FREQUENCY) => {
          setValue('frequency', value);
          track(AMP_KEYS.SEARCH_FILTER_CHANGE, {
            filter: 'frequency',
            value: ChartFrequencyMap.get(value),
          });
        },
        updateAiKeywordFilter: (value: string) => {
          setValue('aiKeywordFilter', value);
          track(AMP_KEYS.SEARCH_FILTER_CHANGE, {
            filter: 'prompt',
            value: value,
          });
        },
        updateKeywordFilter: (
          value: StructuredFilterStringNumberValueSet | null,
        ) => {
          setValue('keywordFilter', value);
          track(AMP_KEYS.SEARCH_FILTER_CHANGE, {
            filter: 'keyword',
            value: value?.values.join(','),
          });
        },
        updateDataFilter: (value: StructuredFilterQueryFormatT[]) => {
          setValue('filterQueryFormats', value);
          track(AMP_KEYS.SEARCH_FILTER_CHANGE, {
            filter: 'feedbackFilter',
            value: value.map((v) => v.columnKey).join(','),
          });
        },
        updateDateFilter: (value: SearchDateFilterT) => {
          setValue('dateRange', value);
          track(AMP_KEYS.SEARCH_FILTER_CHANGE, {
            filter: 'dateRange',
            value:
              value[0].format(DATE_FORMAT.base) +
              ' ~ ' +
              value[1].format(DATE_FORMAT.base),
          });
        },
        updateOriginalFilterQueryFormats: (
          value: StructuredFilterQueryFormatT[],
        ) => setValue('originalFilterQueryFormats', value),
        updateOriginalDateRange: (value: SearchDateFilterT) =>
          setValue('originalDateRange', value),
        updateOriginalAIKeywordFilter: (value) =>
          setValue('originalAiKeywordFilter', value),
        updateOriginalKeywordFilter: (
          value: StructuredFilterStringNumberValueSet | null,
        ) => setValue('originalKeywordFilter', value),
        clearFilter,
        filterChanged,
        isFilterEmpty,
        columns,
        alias: searchResult.alias,
        searchResult,
      }}
    >
      {children}
    </SearchDetailContext.Provider>
  );
};

const useSearchDetailContext = () => {
  return useContext(SearchDetailContext);
};

export { SearchDetailProvider, useSearchDetailContext };
